import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import {
  Add as CreateIcon,
  Cancel as CancelIcon,
  Done as SaveIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  RemoveRedEye as EyeIcon,
} from '@mui/icons-material';
import { Grid, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Stack } from '@mui/material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';
import { UploadDocumentModal } from './components';

import { UTCToDateTime } from '../../../utils/dates';
import { DocumentType } from '../../../enums';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../../utils/hooks';
import {
  UploadDocumentDTO,
  useGetDocumentsQuery,
  useUpdateDocumentTypeMutation,
  useUploadDocumentMutation,
} from '../../../api';
import type { Document } from '../../../types';
import { ViewDocumentModal } from '../../../components/ViewDocumentModal';
import { permissionsCheck } from '../../../utils/permission';

export const Documents: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditDocuments = permissionsCheck(['WRITE_DOCUMENTS']);

  // api data
  const { data: documents, isLoading: isDocumentsLoading } = useGetDocumentsQuery(
    profileUid ? { profileUid } : skipToken
  );
  const [createDocument, { isLoading: isDocumentUploadLoading }] = useUploadDocumentMutation();
  const [updateDocumentType, { isLoading: isDocumentUpdateTypeLoading }] =
    useUpdateDocumentTypeMutation();

  //variables
  const isLoading = isDocumentsLoading || isDocumentUpdateTypeLoading || isDocumentUploadLoading;

  // edit mode logic
  const [typeValue, setTypeValue] = useState<`${DocumentType}` | ''>('');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [selectredFile, setSelectedFile] = useState<{ url: string; fileType: string } | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const [openViewDocument, setOpenViewDocument] = useState(false);

  const startEditMode = (document: Document) => {
    setTypeValue(document.type);
    setActiveRowId(document.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setTypeValue('');
    setActiveRowId(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditDocuments &&
      setSpeedDialButtonComponent([
        { icon: <CreateIcon />, onClick: openUploadModal, disabled: isEditMode },
      ]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, [isEditMode]);

  // fields logic
  const [editFields, setEditFields] = useState<UploadDocumentDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
    isFile?: boolean
  ) => {
    const name = e.target.name;
    // @ts-ignore
    const value = isFile ? e.target.files[0] : e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // add document modal logic
  const [uploadModalShown, setUploadModalShown] = useState(false);
  const openUploadModal = () => {
    setUploadModalShown(true);
  };
  const closeUploadModal = () => {
    setUploadModalShown(false);
    setEditFields(null);
  };

  // upload document handler
  const uploadDocument = async () => {
    if (!editFields || !profileUid) return;

    const formData = new FormData();
    formData.append('file', editFields.file);
    formData.append('type', editFields.type);
    formData.append('mode', editFields.mode);

    const args = { profileUid, body: formData };

    try {
      // @ts-ignore
      await createDocument(args).unwrap();
      closeUploadModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // edit document handler
  const editDocumentType = async () => {
    if (!typeValue || !profileUid || !activeRowId) return;

    const args = {
      profileUid,
      documentId: activeRowId!,
      body: { type: typeValue },
    };

    try {
      await updateDocumentType(args).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // get path for downloading
  const getPath = (path: string) => {
    if (path.startsWith('http') || path.startsWith('https')) {
      return path;
    }

    return `https://${path}`;
  };

  // elements
  const documentSelectOptions = Object.values(DocumentType).map((it) => {
    return (
      <MenuItem key={it} value={it}>
        {t(`common.document-type.${it}`)}
      </MenuItem>
    );
  });

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('profile-documents.head-cells.created-at'),
    },
    {
      id: '2',
      label: t('profile-documents.head-cells.type'),
    },
    {
      id: '3',
      label: t('profile-documents.head-cells.actions'),
      hidden: !hasPermissionToEditDocuments,
    },
  ];

  const rows: Row[] | undefined = documents?.documents?.map((it) => {
    const { id, created_at, type, path } = it;

    const cells: RowCell[] = [
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'type',
        value: type,
        formatting: (value: string) => {
          return t(`common.document-type.${value}`);
        },
        component:
          isEditMode && activeRowId === id ? (
            <Select
              name="type"
              value={typeValue}
              onChange={(e) => setTypeValue(e.target.value as `${DocumentType}`)}
              variant="standard"
              sx={{ width: '320px' }}
              disabled={isLoading}
            >
              {documentSelectOptions}
            </Select>
          ) : undefined,
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditDocuments,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => editDocumentType()}
                disabled={isLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="open"
              color="warning"
              onClick={() => {
                const splitFileName = path?.split('.');
                const fileType = splitFileName[splitFileName.length - 1];
                setSelectedFile({
                  url: getPath(path),
                  fileType: fileType,
                });
                setOpenViewDocument(true);
              }}
              disabled={isLoading}
            >
              <EyeIcon />
            </IconButton>
            <IconButton
              aria-label="download"
              color="info"
              disabled={isLoading}
              download
              href={getPath(path)}
            >
              <DownloadIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <UploadDocumentModal
        open={uploadModalShown}
        onClose={closeUploadModal}
        onUpload={uploadDocument}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isDocumentUploadLoading}
      />
      <ViewDocumentModal
        onClose={() => setOpenViewDocument(false)}
        item={selectredFile}
        open={openViewDocument}
      />
    </>
  );
};
